import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Settings, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    site_name: 'Finest Feathers',
    tagline: 'Amplifying Indigenous Voices in Creative Spaces',
    location: 'Santa Rosa, California',
    tribal_affiliation: 'Pomo / Karuk',
    email: 'finestfeathersproductions@gmail.com',
    social_links: {
      instagram: '',
      tiktok: '',
      youtube: '',
      apple_music: '',
      spotify: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`);
      const data = await response.json();
      if (data) {
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSocialChange = (platform, value) => {
    setSettings(prev => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [platform]: value
      }
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...settings, type: 'general' })
      });

      if (response.ok) {
        alert('Settings saved successfully!');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center text-gray-400 py-12">Loading settings...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Site Settings</h1>
          <p className="text-gray-400">Manage your website configuration</p>
        </div>

        {/* General Settings */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-orange-500" />
              General Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2 text-sm">Site Name</label>
                <input
                  type="text"
                  value={settings.site_name}
                  onChange={(e) => handleChange('site_name', e.target.value)}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-white mb-2 text-sm">Email</label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-white mb-2 text-sm">Tagline</label>
              <input
                type="text"
                value={settings.tagline}
                onChange={(e) => handleChange('tagline', e.target.value)}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2 text-sm">Location</label>
                <input
                  type="text"
                  value={settings.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-white mb-2 text-sm">Tribal Affiliation</label>
                <input
                  type="text"
                  value={settings.tribal_affiliation}
                  onChange={(e) => handleChange('tribal_affiliation', e.target.value)}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Social Links */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Social Media Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2 text-sm">Instagram URL</label>
                <input
                  type="text"
                  value={settings.social_links?.instagram || ''}
                  onChange={(e) => handleSocialChange('instagram', e.target.value)}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                  placeholder="https://www.instagram.com/..."
                />
              </div>
              <div>
                <label className="block text-white mb-2 text-sm">TikTok URL</label>
                <input
                  type="text"
                  value={settings.social_links?.tiktok || ''}
                  onChange={(e) => handleSocialChange('tiktok', e.target.value)}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                  placeholder="https://www.tiktok.com/@..."
                />
              </div>
              <div>
                <label className="block text-white mb-2 text-sm">YouTube URL</label>
                <input
                  type="text"
                  value={settings.social_links?.youtube || ''}
                  onChange={(e) => handleSocialChange('youtube', e.target.value)}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                  placeholder="https://www.youtube.com/@..."
                />
              </div>
              <div>
                <label className="block text-white mb-2 text-sm">Apple Music URL</label>
                <input
                  type="text"
                  value={settings.social_links?.apple_music || ''}
                  onChange={(e) => handleSocialChange('apple_music', e.target.value)}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                  placeholder="https://music.apple.com/..."
                />
              </div>
              <div>
                <label className="block text-white mb-2 text-sm">Spotify URL</label>
                <input
                  type="text"
                  value={settings.social_links?.spotify || ''}
                  onChange={(e) => handleSocialChange('spotify', e.target.value)}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                  placeholder="https://open.spotify.com/..."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Admin Credentials Info */}
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardHeader>
            <CardTitle className="text-white">Admin Credentials</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-400 text-sm mb-4">
              Your current admin login credentials:
            </p>
            <div className="bg-zinc-900 p-4 rounded-lg space-y-2">
              <p className="text-white"><strong className="text-orange-500">Username:</strong> admin</p>
              <p className="text-white"><strong className="text-orange-500">Password:</strong> finestfeathers2026</p>
            </div>
            <p className="text-gray-500 text-xs mt-4">
              To change admin credentials, please contact support.
            </p>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600 text-black w-full py-6 text-lg"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
